import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { useBetreuer } from 'api/betreuer';
import { useKundeList } from 'api/kunde';
import { SelectOption } from 'formComponents/Select/types';
import classnames from 'classnames';
import { viewStyles } from 'styles/viewStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Select } from 'formComponents/Select/Select';
import { ValueType } from 'react-select';
import { Bestellung, BestellungFilter, useBestellungList } from 'api/bestellung';
import { BestellungItem } from 'components/Bestellung/BestellungItem';
import useDebouncedState from 'hooks/useDebouncedState';
import { BestellungPosition, BestellungPositionFilter, useBestellungPositionList } from 'api/bestellungPosition';
import { DebouncedSearchBox } from 'formComponents/DebouncedSearchBox';
import { BestellungEdit, BestellungEditData } from 'components/Bestellung/BestellungEdit';
import { List } from '@fluentui/react/lib/List';
import AutoSizer from 'react-virtualized-auto-sizer';
import moment from 'moment-timezone';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import usePersistentState from 'hooks/usePersistentState';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { useHistory, useParams } from 'react-router-dom';
import { RecordViewUrlParams } from 'layout/App/types';
import { createRecordUrl, decodeRecordNr } from 'utils/formatter';
import { BestellungPositionenHeader } from 'components/Bestellung/BestellungPositionenHeader';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { BestellungStatusMulti } from 'formComponents/lookups/BestellungStatusMulti';

const defaultMaxRows = 250;

const styles = mergeStyleSets({
    /*
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 150px 180px 170px 170px 5fr 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
     */
    searchBar1: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 150px 180px 5fr 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBar2: {
        display: 'grid',
        gridTemplateColumns: '170px 170px max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

interface FilterValues {
    maxRows: number;
    kNr?: number;
    nr?: string;
    auNr?: string;
    status: string;
    statusList: number[];
    betreuer: SelectOption;
    datumVon?: string;
    datumBis?: string;
    lArt: string;
    searchText: string;
    faelligeLieferungen: boolean;
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    kNr: undefined,
    nr: undefined,
    auNr: '',
    status: '__all__',
    statusList: [],
    betreuer: alleBetreuerOption,
    datumVon: undefined,
    datumBis: undefined,
    lArt: '__all__',
    searchText: '',
    faelligeLieferungen: false,
};

export const BestellungView = (): JSX.Element => {
    const auth = useSelector(authSelector);
    const history = useHistory();
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const [viewMode, setViewMode] = usePersistentState<'short' | 'long'>('long', 'BestellungView.viewMode');

    const { urlKundeNr, urlRecordNr } = useParams<RecordViewUrlParams>();
    const kNr = urlKundeNr ? parseInt(urlKundeNr, 10) : undefined;

    /*
    const isSmallScreenEmbedded = useMediaQuery({ query: `(max-width: 2100px)` });
    const isSmallScreenNotEmbedded = useMediaQuery({ query: `(max-width: 1750px)` });
    const isSmallScreen = urlKundeNr ? isSmallScreenEmbedded : isSmallScreenNotEmbedded;
     */

    const bestellungEditData = React.useMemo<BestellungEditData | null>(() => {
        if (urlRecordNr) {
            return {
                bNr: decodeRecordNr(urlRecordNr),
                kNr,
            };
        }
        return null;
    }, [kNr, urlRecordNr]);

    const kundeList = useKundeList({ nr: kNr || -1 });

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
        if (auth.rolle === 'AD') {
            return {
                ...defaultFilterValues,
                betreuer: {
                    value: auth.kz || '',
                    label: auth.name || '?',
                },
            };
        }
        return {
            ...defaultFilterValues,
            betreuer: alleBetreuerOption,
        };
    }, 1000);

    const filter = React.useMemo<BestellungFilter>(() => {
        return {
            kNr,
            betreuer:
                debouncedFilterValues.betreuer.value === '__all__'
                    ? undefined
                    : parseInt(debouncedFilterValues.betreuer.value, 10),
            // status: debouncedFilterValues.status === '__all__' ? undefined :
            // parseInt(debouncedFilterValues.status, 10),
            statusList: debouncedFilterValues.statusList,
            nrSearch: debouncedFilterValues.nr,
            auNrSearch: debouncedFilterValues.auNr,
            datumVon: debouncedFilterValues.datumVon,
            datumBis: debouncedFilterValues.datumBis,
            faelligeLieferungen: debouncedFilterValues.faelligeLieferungen,
            lArt: debouncedFilterValues.lArt === '__all__' ? undefined : debouncedFilterValues.lArt,
            searchText: debouncedFilterValues.searchText,
            withKunde: true,
            maxRows: defaultMaxRows,
        };
    }, [kNr, debouncedFilterValues]);

    const bestellungList = useBestellungList(filter);

    const positionenFilter = React.useMemo<BestellungPositionFilter>(() => {
        if (bestellungList.isLoading || bestellungList.isLoading || bestellungList.isError) {
            return {};
        }

        return {
            bNrList: bestellungList.data?.map((bestellung) => bestellung.nr) || [],
        };
    }, [bestellungList]);

    const bestellungPositionList = useBestellungPositionList(positionenFilter, { enabled: false });

    React.useEffect(() => {
        if (positionenFilter.bNrList) {
            bestellungPositionList.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionenFilter]);

    const bestellungPositionenByANr = React.useMemo<{ [key: string]: BestellungPosition[] }>(() => {
        if (
            bestellungPositionList.isLoading ||
            bestellungPositionList.isLoading ||
            bestellungPositionList.isError ||
            (bestellungPositionList.data?.length || 0) === 0
        ) {
            return {};
        }

        const newBestellungPositionenByANr: { [key: string]: BestellungPosition[] } = {};

        bestellungPositionList.data?.forEach((bestellungPosition) => {
            if (!newBestellungPositionenByANr[bestellungPosition.bNr]) {
                newBestellungPositionenByANr[bestellungPosition.bNr] = [];
            }

            newBestellungPositionenByANr[bestellungPosition.bNr].push(bestellungPosition);
        });

        return newBestellungPositionenByANr;
    }, [bestellungPositionList]);

    const onRenderCell = React.useCallback(
        (bestellung: Bestellung | undefined): JSX.Element => {
            if (!bestellung) {
                return <div />;
            }

            return (
                <BestellungItem
                    key={bestellung.nr}
                    bestellung={bestellung}
                    withKunde={!urlKundeNr}
                    viewMode={viewMode}
                    withHeader={false}
                    bestellungPositionen={bestellungPositionenByANr[bestellung.nr] || []}
                    editBestellung={() => history.push(createRecordUrl('bestellungen', urlKundeNr, bestellung.nr))}
                />
            );
        },
        [bestellungPositionenByANr, urlKundeNr, viewMode, history],
    );

    const renderDatumVon = () => (
        <FormDatePicker
            placeholder="Datum (von)"
            value={filterValues.datumVon}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    const renderDatumBis = () => (
        <FormDatePicker
            placeholder="Datum (bis)"
            value={filterValues.datumBis}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    if (auth.rolle === 'AD' && kundeList.data?.length === 1) {
        if (kundeList.data[0].betreuer && kundeList.data[0].betreuer !== auth.nr) {
            return (
                <Fabric className={viewStyles.container}>
                    <div className={viewStyles.listContainer}>
                        <div className={viewStyles.noData}>Keine Daten.</div>
                    </div>
                </Fabric>
            );
        }
    }

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric
            className={classnames(viewStyles.container, viewStyles.twoSearchBarLines, {
                [viewStyles.embedded]: !!urlKundeNr,
            })}
        >
            <Helmet>
                {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                <title>{bestellungEditData?.bNr ? 'Bestellung ' + bestellungEditData?.bNr : 'Bestellungen'}</title>
            </Helmet>
            <div className={styles.searchBar1}>
                <DebouncedSearchBox
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            searchText: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Bestellung-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            nr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Auftrag-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            auNr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <LieferArt
                    value={filterValues.lArt || ''}
                    addAll
                    menuPlacement="bottom"
                    renderLabel="placeholder"
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            lArt: newValue,
                        });
                    }}
                />
                <Select
                    name="betreuer"
                    value={filterValues.betreuer}
                    options={betreuerFilterOptions}
                    onChange={(newValue: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            betreuer: newValue || alleBetreuerOption,
                        });
                    }}
                />

                <BestellungStatusMulti
                    value={filterValues.statusList.map((v) => v.toString())}
                    renderLabel="placeholder"
                    onChange={(newValues) => {
                        setFilterValues({
                            ...filterValues,
                            statusList: newValues.map((v) => parseInt(v, 10)),
                        });
                    }}
                />

                <div />

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Kurzansicht"
                        checked={viewMode === 'short'}
                        onChange={(event, checked) => {
                            setViewMode(checked ? 'short' : 'long');
                        }}
                    />
                </div>

                <PrimaryButton
                    onClick={() => history.push(createRecordUrl('bestellungen', urlKundeNr, 'neu'))}
                    text="Neue Bestellung"
                />
            </div>

            <div className={styles.searchBar2}>
                {renderDatumVon()}
                {renderDatumBis()}
                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Fällige Lieferungen"
                        checked={filterValues.faelligeLieferungen}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                faelligeLieferungen: !!checked,
                            });
                        }}
                    />
                </div>
            </div>

            {bestellungList.isLoading || bestellungList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <div className={viewStyles.listContainer}>
                    {bestellungList?.data && bestellungList?.data.length === 0 ? (
                        <div className={viewStyles.noData}>Keine Bestellungen.</div>
                    ) : (
                        <>
                            {viewMode === 'short' && (
                                <div className={viewStyles.listContainer}>
                                    <table className={formStyles.stickyPositionenHeaderTable}>
                                        <BestellungPositionenHeader />
                                    </table>
                                </div>
                            )}
                            <AutoSizer>
                                {({ height, width }) => (
                                    <div
                                        style={{ width, height: height - 25, overflowX: 'hidden', overflowY: 'scroll' }}
                                    >
                                        <List
                                            key={viewMode + (bestellungPositionList.isSuccess ? 1 : 0)}
                                            items={bestellungList.data || []}
                                            onRenderCell={onRenderCell}
                                        />
                                    </div>
                                )}
                            </AutoSizer>
                        </>
                    )}
                </div>
            )}
            {bestellungEditData !== null && (
                <BestellungEdit
                    defaultKNr={bestellungEditData.kNr}
                    bNr={bestellungEditData.bNr}
                    onClose={(refetch) => {
                        history.push(createRecordUrl('bestellungen', urlKundeNr));
                        if (refetch) {
                            bestellungPositionList.refetch().then(() => {
                                bestellungList.refetch().then();
                            });
                        }
                    }}
                    onReopen={(newANr: string) => {
                        history.push(createRecordUrl('bestellungen', urlKundeNr));
                        bestellungPositionList.refetch().then(() => {
                            bestellungList.refetch().then(() => {
                                history.push(createRecordUrl('bestellungen', urlKundeNr, newANr));
                            });
                        });
                    }}
                />
            )}
        </Fabric>
    );
};
